import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, ChatCircleText, CheckCircle, Code, Eye, FilePdf, Gauge, Globe,
  Lightning, Lock, MagnifyingGlass, ShieldCheck, Sparkle,
} from "@phosphor-icons/react";
import PublicHeader from "@/components/PublicHeader";
import Footer from "@/components/Footer";
import PDFStack from "@/components/PDFStack";

const CATEGORIES = [
  {
    icon: MagnifyingGlass,
    title: "SEO",
    n: 11,
    checks: [
      "Title tag presence + 30–60 character length",
      "Meta description presence + 70–160 character length",
      "Single H1 tag per page",
      "Canonical link declaration",
      "Viewport meta tag for mobile",
      "html[lang] attribute set",
      "Open Graph (og:title, og:description, og:image)",
      "Twitter Card meta",
      "robots.txt reachable at site root",
      "sitemap.xml reachable at site root",
      "Indexability signals consistent across the page",
    ],
  },
  {
    icon: Sparkle,
    title: "GEO — Generative Engine Optimisation",
    n: 10,
    checks: [
      "llms.txt present at site root (LLM content map)",
      "JSON-LD structured data on the page",
      "Organization or Person schema for entity grounding",
      "Author / byline visible (E-E-A-T)",
      "datePublished / dateModified markers",
      "About & Contact pages linked from homepage",
      "≥300 words of server-rendered text (AI bots don't run JS)",
      "Topic clusters: ≥3 H2 sections",
      "External authoritative references / citations",
      "AI crawlers (GPTBot, ClaudeBot, PerplexityBot) permitted",
    ],
  },
  {
    icon: ChatCircleText,
    title: "AEO — Answer Engine Optimisation",
    n: 9,
    checks: [
      "FAQPage / QAPage / Question schema",
      "HowTo schema for step-by-step content",
      "Speakable schema for voice assistants",
      "Question-style H2/H3 sub-headings",
      "Snippet-sized opening paragraphs (40–60 words)",
      "Structured lists or comparison tables",
      "Definition-style opener (\"X is Y\")",
      "TL;DR / Key-takeaways block",
      "BreadcrumbList schema",
    ],
  },
  {
    icon: Lightning,
    title: "Performance",
    n: 8,
    checks: [
      "Time to first byte under 800 ms",
      "HTML payload size under 200 KB",
      "External scripts count (≤ 15)",
      "External stylesheets count (≤ 6)",
      "Image lazy-loading coverage",
      "Compression (gzip / brotli) enabled",
      "Cache-Control headers present",
      "Redirect chain depth (≤ 1)",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Security",
    n: 9,
    checks: [
      "HTTPS in use",
      "Strict-Transport-Security (HSTS) header",
      "Content-Security-Policy header",
      "X-Content-Type-Options: nosniff",
      "X-Frame-Options to prevent clickjacking",
      "Referrer-Policy set",
      "Permissions-Policy set",
      "Server / X-Powered-By version leaks hidden",
      "Valid SSL certificate + no mixed-content asset links",
    ],
  },
  {
    icon: CheckCircle,
    title: "Accessibility",
    n: 7,
    checks: [
      "All images carry alt text",
      "All form fields have labels or aria-label",
      "All buttons have accessible names",
      "All links have descriptive text",
      "html[lang] attribute set",
      "Skip-to-content link present",
      "Semantic landmarks (header / nav / main / footer)",
    ],
  },
  {
    icon: Code,
    title: "Tech Stack Detection",
    n: 12,
    checks: [
      "Server software & runtime fingerprint (Apache, nginx, LiteSpeed, IIS, Caddy)",
      "Hosting / PaaS (Vercel, Netlify, Cloudflare Pages, Heroku, AWS, GCP, cPanel)",
      "CDN & edge (Cloudflare, Fastly, Akamai, CloudFront, BunnyCDN, KeyCDN, jsDelivr)",
      "CMS (WordPress, Drupal, Shopify, Wix, Squarespace, Webflow, Ghost, Magento, HubSpot, Sitecore)",
      "Headless CMS (Contentful, Sanity, Strapi)",
      "JS frameworks & libraries (Next, Nuxt, Gatsby, Remix, Astro, SvelteKit, React, Vue, Angular, Svelte, Alpine)",
      "CSS frameworks & UI libs (Bootstrap, Tailwind, Bulma, Material UI, Chakra, Ant Design, shadcn)",
      "Analytics & RUM (GA4, Plausible, Fathom, Matomo, Mixpanel, Segment, Heap, Amplitude, Hotjar, FullStory, Clarity, Sentry, New Relic, Datadog)",
      "Advertising pixels (Google Ads, Meta, LinkedIn, TikTok, X, Pinterest, Reddit, Bing UET)",
      "Marketing & email (HubSpot, Marketo, Mailchimp, Klaviyo, ActiveCampaign, ConvertKit, OptinMonster)",
      "Live chat & support (Intercom, Drift, Crisp, Zendesk, Tawk, LiveChat, Tidio)",
      "Payments, video, forms, fonts, icons, A/B testing, search, reviews, captcha — 100+ patterns total",
    ],
  },
];

const SISTER = [
  {
    name: "Yellow Thinking",
    href: "https://www.yellowthinking.com",
    role: "The parent group",
    blurb:
      "Branding, websites, social media, print, TV and cinema — Yellow Thinking is a Toronto creative studio that helps ambitious businesses look and feel like the brand they want to be. If Auditor flags something we cannot fix in code alone, the Yellow Thinking team is the one who reshapes the strategy around it.",
    services: ["Branding & visual identity", "Websites & e-commerce", "Social media campaigns", "Print, TV & cinema"],
  },
  {
    name: "fasterbetter.ca",
    href: "https://www.fasterbetter.ca",
    role: "Speed & hosting",
    blurb:
      "When Auditor scores you low on performance, fasterbetter.ca is the team that fixes it. Themeless WordPress builds, managed cloud hosting, real-time security and CDN — everything tuned so your site loads fast and stays fast, with no plugin bloat and no surprise downtime.",
    services: ["Themeless WordPress builds", "Managed Google Cloud / AWS hosting", "CDN + WebP image optimisation", "Realtime malware scanning"],
  },
  {
    name: "webcoders.ca",
    href: "https://www.webcoders.ca",
    role: "Custom builds",
    blurb:
      "Creative coding for creative sites. When a template will not do — when you need an interactive portfolio, a custom dashboard, a bespoke booking flow, or a marketing site that does something unusual — webcoders.ca writes it from scratch. Functional, beautiful, and yours to keep.",
    services: ["Custom website development", "Interactive & motion design", "Bespoke web apps", "API integrations"],
  },
];

export default function AboutPage() {
  const total = CATEGORIES.reduce((s, c) => s + c.n, 0);
  return (
    <div className="min-h-screen bg-background">
      <PublicHeader active="about" />

      <section className="border-b border-border">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="label-overline mb-4">About Auditor</div>
            <h1 className="font-display text-5xl lg:text-6xl font-black tracking-tighter leading-[1] mb-6">
              The honest second opinion <br />
              <span className="text-brand">your website deserves.</span>
            </h1>
            <p className="text-lg text-ink-muted leading-relaxed mb-4 max-w-2xl">
              Auditor was built for the moment every site owner reaches eventually:
              {`"I think there's something wrong, but I don't know what to look for."`}
              We do not sell theory. We point a browser at your URL, run a
              sixty-point inspection (now including GEO &amp; AEO — so AI
              search engines can actually cite you), and hand you a prioritised
              list of the things actually slowing you down, locking visitors
              out, or quietly costing you Google rankings.
            </p>
            <p className="text-lg text-ink-muted leading-relaxed max-w-2xl">
              No fluff. No vanity scores. Each finding is paired with a fix you can
              hand to a developer (or to one of our sister teams) and check off the
              same week.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-3">
            <Stat n={`${total}`} label="checks per audit" />
            <Stat n="7" label="report categories" />
            <Stat n="~30s" label="average run time" />
            <Stat n="0" label="add-ons required" />
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-white">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20">
          <div className="grid lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-5">
              <div className="label-overline mb-4">What we check</div>
              <h2 className="font-display text-4xl lg:text-5xl font-black tracking-tight leading-tight">
                Sixty checks. <br /> Seven categories.
              </h2>
            </div>
            <p className="lg:col-span-7 text-ink-muted text-lg leading-relaxed pt-2">
              Below is the entire inspection list, grouped the same way the report
              shows them. Every check returns a clear pass, warn, or fail — and a
              one-line note explaining why it matters.
            </p>
          </div>
          <div className="space-y-4">
            {CATEGORIES.map(({ icon: Icon, title, n, checks }) => (
              <div key={title} data-testid={`about-cat-${title.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className="surface-card overflow-hidden">
                <div className="flex items-center gap-4 px-6 py-4 border-b border-border bg-secondary">
                  <Icon size={24} className="text-brand" weight="duotone" />
                  <div className="flex-1">
                    <div className="font-display font-bold text-xl">{title}</div>
                    <div className="text-xs text-ink-muted mono">{n} checks</div>
                  </div>
                </div>
                <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 px-6 py-5">
                  {checks.map((c) => (
                    <li key={c} className="flex items-start gap-2 text-sm">
                      <CheckCircle size={14} className="text-success mt-0.5 shrink-0" weight="fill" />
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-white">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="relative h-[500px] lg:h-[560px]" data-testid="about-pdf-stack">
              <PDFStack className="h-full" />
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="label-overline mb-4">The deliverable</div>
            <h2 className="font-display text-4xl lg:text-5xl font-black tracking-tighter leading-tight mb-6">
              A designed PDF, <br /> not a Lighthouse dump.
            </h2>
            <p className="text-lg text-ink-muted leading-relaxed mb-4">
              Every audit returns a seven-page printable report. Branded cover.
              Score gauges for each category. A prioritised five-step fix-list.
              Quick wins for today and long-term improvements for next quarter.
            </p>
            <p className="text-base text-ink-muted leading-relaxed mb-6">
              Send it straight to a client. Print it for a project kick-off.
              Drop it into a strategy deck. The report is built to look at home
              wherever it lands.
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 border border-border rounded-sm text-xs mono text-ink-muted">
                <FilePdf size={12} weight="duotone" /> PDF
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 border border-border rounded-sm text-xs mono text-ink-muted">
                JSON
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 border border-border rounded-sm text-xs mono text-ink-muted">
                Branded header & footer
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 border border-border rounded-sm text-xs mono text-ink-muted">
                7 pages
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20">
          <div className="mb-12 max-w-3xl">
            <div className="label-overline mb-4">The wider team</div>
            <h2 className="font-display text-4xl lg:text-5xl font-black tracking-tight leading-tight mb-4">
              Auditor finds the issue. <br />
              Our sister studios fix it.
            </h2>
            <p className="text-lg text-ink-muted leading-relaxed">
              Auditor is one of four studios that sit under{" "}
              <a href="https://www.yellowthinking.com" target="_blank" rel="noopener noreferrer"
                className="text-ink underline-offset-2 hover:underline hover:text-brand transition-colors" data-testid="about-yt-inline-link">
                Yellow Thinking
              </a>. If your report points to a problem we can identify but you do not
              have an in-house team to fix, the rest of the group is one phone
              call away.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            {SISTER.map((s) => (
              <div key={s.name} data-testid={`about-sister-${s.name.replace(/[^a-z]/gi, "").toLowerCase()}`}
                className="surface-card p-6 lg:p-7 flex flex-col">
                <div className="label-overline mb-2">{s.role}</div>
                <a href={s.href} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-display text-2xl font-black tracking-tight mb-3 hover:text-brand transition-colors group">
                  {s.name}
                  <ArrowUpRight size={18} className="opacity-60 group-hover:opacity-100" weight="bold" />
                </a>
                <p className="text-sm text-ink-muted leading-relaxed mb-5">{s.blurb}</p>
                <ul className="space-y-1.5 mt-auto pt-4 border-t border-border">
                  {s.services.map((svc) => (
                    <li key={svc} className="text-xs text-ink flex items-center gap-1.5">
                      <span className="w-1 h-1 bg-brand rounded-full" /> {svc}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink text-white">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 lg:py-20 grid lg:grid-cols-2 gap-8 items-center">
          <h2 className="font-display text-4xl lg:text-5xl font-black tracking-tight leading-tight">
            Run your first audit. <br /> It is free.
          </h2>
          <div className="flex flex-wrap items-center gap-3 lg:justify-end">
            <Link to="/register" data-testid="about-cta-register"
              className="inline-flex items-center gap-2 bg-white text-ink px-6 py-4 rounded-sm font-semibold hover:bg-brand hover:text-white transition-colors">
              Start free <ArrowRight size={16} />
            </Link>
            <Link to="/contact" data-testid="about-cta-contact"
              className="inline-flex items-center gap-2 border border-white/40 px-6 py-4 rounded-sm font-semibold hover:bg-white hover:text-ink transition-colors">
              Talk to a human
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Stat({ n, label }) {
  return (
    <div className="surface-card p-5 flex items-baseline justify-between">
      <span className="num-display text-4xl">{n}</span>
      <span className="text-xs text-ink-muted mono">{label}</span>
    </div>
  );
}
